import { OntologyValueType } from 'features/dynamicOntology'
import isPlainObject from 'lodash/isPlainObject'
import pickBy from 'lodash/pickBy'
import { Dispatch, SetStateAction, useMemo } from 'react'

function resolveSetState<T>(action: SetStateAction<T>, previousValue: T): T {
  return typeof action === 'function'
    ? (action as (value: T) => T)(previousValue)
    : action
}

function createSubsetSetter(
  setValue:
    | Dispatch<SetStateAction<Record<string, unknown> | undefined>>
    | undefined,
  predicate: (key: string) => boolean
): Dispatch<SetStateAction<OntologyValueType | undefined>> | undefined {
  if (!setValue) {
    return undefined
  }

  return (action: SetStateAction<OntologyValueType | undefined>) => {
    setValue((prev) => {
      if (!prev) {
        return undefined
      }

      const prevSubset = pickBy(prev, (_value, key) => predicate(key)) as
        | OntologyValueType
        | undefined

      const nextSubset = resolveSetState<OntologyValueType | undefined>(
        action,
        prevSubset
      )

      const nextData = { ...prev }

      if (nextSubset === undefined) {
        Object.keys(prevSubset ?? {}).forEach((key) => {
          delete nextData[key]
        })
        return nextData
      }

      if (!isPlainObject(nextSubset)) {
        return prev
      }

      const filteredNextSubset = pickBy(
        nextSubset as Record<string, unknown>,
        (_value, key) => predicate(key)
      ) as Record<string, unknown>

      Object.keys(prevSubset ?? {}).forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(filteredNextSubset, key)) {
          delete nextData[key]
        }
      })

      Object.entries(filteredNextSubset).forEach(([key, value]) => {
        nextData[key] = value
      })

      return nextData
    })
  }
}

/**
 * Splits a root object + setter into two slices keyed by a predicate.
 *
 * The hook derives two read / write pairs:
 *   - the first for keys that satisfy `keyPred`
 *   - the second for the remaining keys
 *
 * Setter updates can be expressed as direct values or functional updates, and
 * the hook takes care of pruning keys removed by the child updaters while
 * leaving untouched keys intact on the root object.
 *
 * @param value Current root object value.
 * @param setValue Setter that mutates the root object.
 * @param keyPred Predicate indicating which keys belong in the "matching" slice.
 * @returns A tuple containing the matching slice/value pair followed by the non-matching pair.
 */
export function useSplitObjectState(
  value: Record<string, unknown>,
  setValue:
    | Dispatch<SetStateAction<Record<string, unknown> | undefined>>
    | undefined,
  keyPred: (key: string) => boolean
): [
  [
    OntologyValueType,
    Dispatch<SetStateAction<OntologyValueType | undefined>> | undefined,
  ],
  [
    OntologyValueType,
    Dispatch<SetStateAction<OntologyValueType | undefined>> | undefined,
  ],
] {
  const matchingValue = useMemo(() => {
    return pickBy(value, (_val, key) => keyPred(key)) as OntologyValueType
  }, [keyPred, value])

  const nonMatchingValue = useMemo(() => {
    return pickBy(value, (_val, key) => !keyPred(key)) as OntologyValueType
  }, [keyPred, value])

  const matchingSetter = useMemo(
    () => createSubsetSetter(setValue, keyPred),
    [keyPred, setValue]
  )

  const nonMatchingSetter = useMemo(
    () => createSubsetSetter(setValue, (key) => !keyPred(key)),
    [keyPred, setValue]
  )

  return [
    [matchingValue, matchingSetter],
    [nonMatchingValue, nonMatchingSetter],
  ]
}
